import MapboxUtil from 'components/map/sd-mapbox.util';
import { getSystemFlags } from 'core/core.selectors';
import type { Feature, GeoJsonProperties, Point } from 'geojson';
import type mapboxgl from 'mapbox-gl';
import { RegionalOverviewSource } from 'querys/regional-overview/regional-overview.query.models';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createRiskMapClusterMarker, type RiskMapClusterColors, type RiskMapClusterCounts } from '../../components/risk-map-cluster';
import { getRiskColor, getRiskOrder, isModelEnabled } from './regional-overview-map-utils';
import { CLUSTER_CONFIG, CLUSTER_PROPERTIES, CLUSTER_SOURCE_ID, toRiskMapClusterCounts } from './use-overview-points-cluster.constants';
import { RegionalRiskLevelColor } from '../regional-risk.models';
import { OVERVIEW_POINTS_HIGHLIGHT_LAYER, OVERVIEW_POINTS_LAYER } from './use-render-map-disease-risk-field';

const REGIONAL_RISK_CLUSTER_COLORS: RiskMapClusterColors = {
  high: RegionalRiskLevelColor.HIGH_RISK,
  medium: RegionalRiskLevelColor.MEDIUM_RISK,
  low: RegionalRiskLevelColor.LOW_RISK,
  noRisk: RegionalRiskLevelColor.NO_RISK,
  notApplicable: RegionalRiskLevelColor.MISSING_CONFIG
};

interface UseOverviewPointsClusterProps<T extends GeoJsonProperties> {
  map?: mapboxgl.Map;
  model: RegionalOverviewSource;
  mapPointFeatures?: Feature<Point, T>[];
  onClusterClick?: (clusterId: number, lngLat: mapboxgl.LngLatLike) => void;
}

interface ClusterFeatureProperties {
  cluster_id: number;
  highCount?: number;
  mediumCount?: number;
  lowCount?: number;
  noRiskCount?: number;
  notApplicableCount?: number;
  point_count?: number;
}

/**
 * Supercluster reassigns the same cluster_id to a different set of points when the source data is
 * replaced with the same point count and geometry, which is what the disease and risk-level filters
 * produce (they only recompute calculatedRisk). Comparing the bucket counts distinguishes a genuine
 * reuse from a cluster that kept its id but changed composition and therefore needs its donut redrawn.
 */
const getCountsSignature = (counts: RiskMapClusterCounts): string =>
  `${counts.high}|${counts.medium}|${counts.low}|${counts.noRisk}|${counts.notApplicable}`;

export function useOverviewPointsCluster<T extends GeoJsonProperties>({
  map,
  model,
  mapPointFeatures,
  onClusterClick
}: UseOverviewPointsClusterProps<T>): void {
  const flags = useSelector(getSystemFlags);
  const markersRef = useRef<Map<number, mapboxgl.Marker>>(new Map());
  const clusterCoordsRef = useRef<Map<number, { lng: number; lat: number }>>(new Map());
  const clusterCountsRef = useRef<Map<number, string>>(new Map());
  const onClusterClickRef = useRef(onClusterClick);

  useEffect(() => {
    onClusterClickRef.current = onClusterClick;
  }, [onClusterClick]);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current.clear();
    clusterCoordsRef.current.clear();
    clusterCountsRef.current.clear();
  }, []);

  const renderClusterMarkers = useCallback(() => {
    if (!map) return;
    if (!map.getSource(CLUSTER_SOURCE_ID)) return;

    const features = map.querySourceFeatures(CLUSTER_SOURCE_ID, { filter: ['has', 'point_count'] });
    const activeIds = new Set<number>();

    features.forEach(feature => {
      const properties = feature.properties as ClusterFeatureProperties | null;
      if (properties?.cluster_id === undefined) return;

      const clusterId = properties.cluster_id;
      if (activeIds.has(clusterId)) return;
      activeIds.add(clusterId);

      const { geometry } = feature;
      if (geometry.type !== 'Point') return;

      const [lng, lat] = geometry.coordinates;
      clusterCoordsRef.current.set(clusterId, { lng, lat });

      const counts = toRiskMapClusterCounts(properties);
      const signature = getCountsSignature(counts);

      const existing = markersRef.current.get(clusterId);
      if (existing) {
        if (clusterCountsRef.current.get(clusterId) === signature) {
          existing.setLngLat([lng, lat]);
          return;
        }

        existing.remove();
        markersRef.current.delete(clusterId);
      }

      const { marker, element } = createRiskMapClusterMarker(map, [lng, lat], counts, { colors: REGIONAL_RISK_CLUSTER_COLORS });

      element.addEventListener('click', () => {
        const coords = clusterCoordsRef.current.get(clusterId);
        if (coords) onClusterClickRef.current?.(clusterId, coords);
      });

      markersRef.current.set(clusterId, marker);
      clusterCountsRef.current.set(clusterId, signature);
    });

    markersRef.current.forEach((marker, id) => {
      if (!activeIds.has(id)) {
        marker.remove();
        markersRef.current.delete(id);
        clusterCoordsRef.current.delete(id);
        clusterCountsRef.current.delete(id);
      }
    });
  }, [map]);

  const addClusterLayers = useCallback(() => {
    if (!map) return;

    map.addSource(CLUSTER_SOURCE_ID, {
      type: 'geojson',
      cluster: !!flags?.P40_54240_enable_overview_points_popup,
      generateId: true,
      clusterRadius: CLUSTER_CONFIG.RADIUS,
      clusterMaxZoom: CLUSTER_CONFIG.MAX_ZOOM,
      clusterProperties: CLUSTER_PROPERTIES,
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.addLayer({
      id: OVERVIEW_POINTS_HIGHLIGHT_LAYER,
      type: 'circle',
      source: CLUSTER_SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      maxzoom: CLUSTER_CONFIG.LAYER_MAX_ZOOM,
      paint: {
        'circle-color': 'rgba(255, 255, 255, 0.75)',
        'circle-radius': {
          base: 1,
          stops: [
            [0, 6],
            [3, 10],
            [6, 14],
            [9, 18]
          ]
        },
        'circle-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
        'circle-blur': 0
      }
    });

    map.addLayer({
      id: OVERVIEW_POINTS_LAYER,
      type: 'circle',
      source: CLUSTER_SOURCE_ID,
      filter: ['!', ['has', 'point_count']],
      maxzoom: CLUSTER_CONFIG.LAYER_MAX_ZOOM,
      layout: {
        'circle-sort-key': getRiskOrder()
      },
      paint: {
        'circle-color': getRiskColor(),
        'circle-radius': {
          base: 1,
          stops: [
            [0, 2],
            [3, 4],
            [6, 6],
            [9, 9]
          ]
        },
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 7, 0.7, 8, 0.8],
        'circle-blur': 0.6
      }
    });
  }, [map, flags?.P40_54240_enable_overview_points_popup]);

  const removeClusterLayers = useCallback(() => {
    if (!map) return;

    clearMarkers();
    MapboxUtil.removeLayersIfExists(map, [OVERVIEW_POINTS_HIGHLIGHT_LAYER, OVERVIEW_POINTS_LAYER]);
    MapboxUtil.removeSourcesIfExists(map, [CLUSTER_SOURCE_ID]);
  }, [map, clearMarkers]);

  useEffect(() => {
    if (!isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK) || !map) return () => undefined;

    addClusterLayers();

    return () => {
      removeClusterLayers();
    };
  }, [map, model, addClusterLayers, removeClusterLayers]);

  useEffect(() => {
    if (!map || !isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK)) return;

    const source = map.getSource(CLUSTER_SOURCE_ID) as mapboxgl.GeoJSONSource;

    if (source) {
      source.setData({ type: 'FeatureCollection', features: mapPointFeatures ?? [] });
    }
  }, [map, mapPointFeatures, model]);

  useEffect(() => {
    if (!map || !isModelEnabled(model, RegionalOverviewSource.DISEASE_RISK)) return () => undefined;

    const handleSourceData = (event: mapboxgl.MapSourceDataEvent) => {
      if (event.sourceId !== CLUSTER_SOURCE_ID || !event.isSourceLoaded) return;
      renderClusterMarkers();
    };

    map.on('sourcedata', handleSourceData);
    map.on('render', renderClusterMarkers);
    map.on('moveend', renderClusterMarkers);

    return () => {
      map.off('sourcedata', handleSourceData);
      map.off('render', renderClusterMarkers);
      map.off('moveend', renderClusterMarkers);
      clearMarkers();
    };
  }, [map, model, renderClusterMarkers, clearMarkers]);
}
